import React, { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  useReadUserForTutorialHints,
  useTutorialHintDismissals,
} from "../../services/api";
import { dismissalKey, useTutorialContext } from "./TutorialContext";

/**
 * Bridges the tutorial context to the API.
 *
 * The reducer itself is deliberately free of I/O, so this component owns the
 * two things it needs from the outside world and does them once per mount:
 *
 *  1. loads the user's dismissed hints once and dispatches HYDRATE — until that
 *     lands, the reducer shows nothing at all;
 *  2. keeps the reducer told which workspace is open, so workspace-scoped hints
 *     resolve to the right key.
 *
 * Dismissals are written straight from the click (see useTutorialActions), so
 * there is no queue to drain here. Mounted by TutorialProvider.
 */
const TutorialHydrator: React.FC = () => {
  const { dispatch } = useTutorialContext();
  const { pathname } = useLocation();
  const listDismissals = useTutorialHintDismissals();
  // Needed to namespace the per-tab noPersist store, which outlives a React
  // remount. Without an identity a dismissal made by whoever used this tab
  // before would suppress the incoming user's hints.
  const readUser = useReadUserForTutorialHints();

  // Read the workspace from the path rather than useParams: this component is a
  // sibling of the <Routes> block, not a descendant of the
  // /workspace/:workspace_id/* route, so useParams would always come back empty.
  const workspaceId = useMemo(() => {
    const match = /^\/workspace\/([^/]+)/.exec(pathname);
    return match ? decodeURIComponent(match[1]) : null;
  }, [pathname]);

  // Guards against React 18 StrictMode double-invoking the effect and firing
  // two hydration requests.
  const hydrationStartedRef = useRef(false);

  // 1. Hydrate once per mount.
  //
  // Deliberately no `cancelled` flag here, even though that is the correct
  // pattern for an effect that may legitimately re-run. It is actively wrong
  // when combined with a run-once ref, because StrictMode orders the two like
  // this:
  //
  //   run #1  -> ref = true, fetch starts
  //   cleanup -> would set cancelled = true on the ONLY in-flight request
  //   run #2  -> ref is already true, returns early, starts nothing
  //
  // so the single surviving request resolves into a cancelled closure and
  // HYDRATE never dispatches. `hydrated` then stays false for the whole
  // session and the fail-closed rule silently suppresses every hint — while
  // the server log still shows a successful GET, which makes it look like
  // hydration worked.
  //
  // Dispatching after unmount is safe: TutorialProvider is mounted above this
  // component and outlives it, and a stale dispatch cannot cross a user
  // boundary because BEGIN_HYDRATION resets the store on the next mount.
  useEffect(() => {
    if (hydrationStartedRef.current) return;
    hydrationStartedRef.current = true;

    dispatch({ type: "BEGIN_HYDRATION" });
    (async () => {
      try {
        // Both are prerequisites for showing anything, so run them together
        // rather than serially. Promise.all also means an identity failure
        // fails closed for the same reason a dismissal-list failure does.
        const [user, rows] = await Promise.all([readUser(), listDismissals()]);
        if (!user?.id) return;
        dispatch({
          type: "HYDRATE",
          userId: user.id,
          keys: (rows ?? []).map((r) =>
            dismissalKey(r.hint_id, r.workspace_id),
          ),
        });
      } catch {
        // Fail closed: leave hydrated false so no hint displays this session.
        // Better to miss a hint than to replay one already dismissed elsewhere.
        // Both calls pass suppressNetworkBanner, so this stays silent whether
        // the endpoint 500s or the request never leaves the browser.
      }
    })();
  }, [dispatch, listDismissals, readUser]);

  // 2. Track the open workspace so workspace-scoped hints key correctly.
  useEffect(() => {
    dispatch({ type: "SET_WORKSPACE", workspaceId });
  }, [dispatch, workspaceId]);

  return null;
};

export default TutorialHydrator;
